import type {
  FrontendApi,
  SuccessfulNativeRegistration,
} from '@ory/client-fetch';

import { handleOryCall, logger, setToken } from '@/utils';

interface VerifyRegistrationOtpResult {
  success: boolean;
  oryId?: string;
  error?: string;
}

/**
 * Verifies an OTP for user registration
 * @param ory The Ory client instance
 * @param params Registration verification parameters
 * @returns Result object with success status and identity ID or error
 */
interface VerifyRegistrationOtpParams {
  flowId: string;
  csrfToken: string;
  phoneNumber: string;
  otpCode: string;
}

const verifyRegistrationOtp = async (
  ory: FrontendApi,
  params: VerifyRegistrationOtpParams,
): Promise<VerifyRegistrationOtpResult> => {
  const { flowId, csrfToken, phoneNumber, otpCode } = params;
  logger.debug(`Verifying registration OTP for: ${phoneNumber}`);

  try {
    // Verify the OTP using our utility for handling all response status codes
    const result = await handleOryCall<SuccessfulNativeRegistration>(
      () =>
        ory.updateRegistrationFlow({
          flow: flowId,
          updateRegistrationFlowBody: {
            method: 'code',
            code: otpCode,
            csrf_token: csrfToken,
            traits: {
              phone_number: phoneNumber,
            },
          },
        }),
      'Registration OTP verification',
    );

    // Extract Ory identity ID from response data
    const responseData = result.data;

    // Try to find the identity ID in common locations
    const oryId = responseData?.identity?.id;

    await setToken(true);

    return {
      success: true,
      oryId,
    };
  } catch (error) {
    logger.error(`Registration OTP verification error: ${error}`);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

export { verifyRegistrationOtp };
export type { VerifyRegistrationOtpParams, VerifyRegistrationOtpResult };
